import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function renderHeadline(text) {
  if (!text) return null;
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((p, i) =>
    p.startsWith("*") && p.endsWith("*") && p.length > 2 ? (
      <span key={i} className="font-serif-italic font-normal">{p.slice(1, -1)}</span>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

export default function About() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/about").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <section id="about" className="px-6 md:px-12 lg:px-24 py-32" data-testid="about-section" />;
  }

  const portrait = data.portrait_url?.startsWith("/")
    ? `${BACKEND}${data.portrait_url}`
    : data.portrait_url;

  return (
    <section id="about" className="px-6 md:px-12 lg:px-24 py-32 md:py-48 relative" data-testid="about-section">
      <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-12">
        {data.overline}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
        {portrait && (
          <motion.div
            className="md:col-span-4 md:row-span-2 md:col-start-1"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <div className="img-zoom-wrap">
              <img
                src={portrait}
                alt="Portrait"
                className="w-full h-[60vh] md:h-[75vh] object-cover"
                data-testid="about-portrait-image"
              />
            </div>
          </motion.div>
        )}

        <div className={portrait ? "md:col-span-7 md:col-start-6" : "md:col-span-12"}>
          <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]" data-testid="about-headline">
            {renderHeadline(data.headline)}
          </h2>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-10">
            <div>
              <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-3">
                {data.background_label}
              </p>
              <p className="font-body text-base leading-relaxed text-neutral-700 whitespace-pre-wrap">
                {data.background_text}
              </p>
            </div>
            <div>
              <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-3">
                {data.approach_label}
              </p>
              <p className="font-body text-base leading-relaxed text-neutral-700 whitespace-pre-wrap">
                {data.approach_text}
              </p>
            </div>
          </div>
        </div>

        {data.stats && data.stats.length > 0 && (
          <div className={`${portrait ? "md:col-span-7 md:col-start-6" : "md:col-span-12"} mt-10`}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {data.stats.map((s) => (
                <div key={s.label + s.value} className="border-t border-neutral-300 pt-4" data-testid={`about-stat-${s.label.toLowerCase()}`}>
                  <div className="font-display font-black text-4xl sm:text-5xl tracking-tighter">{s.value}</div>
                  <div className="font-body text-xs uppercase tracking-[0.2em] text-neutral-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
